/**
 * Trainingsstand: Rang je Muskelgruppe (F-34 C3) - rein und getestet.
 * Ein Orientierungsmass aus den eigenen Trainingsdaten, kein objektives
 * Fitnessurteil; es haengt stark davon ab, welche Uebungen erfasst werden.
 *
 * Wert 1-10 (Leitlinie 6, 10 = gut) aus drei Teilen, gewichtet nach dem,
 * was vorliegt:
 *   1. Kraft (50 %): bestes geschaetztes Maximalgewicht der letzten 12 Wochen
 *      im Verhaeltnis zum Koerpergewicht bzw. Wiederholungen/Skill-Stufe,
 *      verglichen mit Schwellen je Uebung.
 *   2. Bestaendigkeit (30 %): Anteil der letzten 12 abgeschlossenen Wochen im Ziel.
 *   3. Entwicklung (20 %): Veraenderung der Kraft ueber 8 Wochen.
 * Rang: Bronze 1-2, Silber 3-4, Gold 5-6, Platin 7-8, Diamant 9-10.
 * Ohne mindestens 4 Wochen Daten und einen Kraft- oder Entwicklungswert:
 * „noch nicht eingestuft“.
 *
 * Schwellen (fuenf Stufen: Einsteiger, Anfaenger, Fortgeschritten, Erfahren,
 * Elite) angelehnt an verbreitete Kraftstandards fuer erwachsene Maenner
 * (Kilgore, Rippetoe & Pendlay, „Practical Programming“; ExRx.net
 * „Strength Standards“; gerundet). Fuer Koerpergewichtsuebungen eigene,
 * runde Richtwerte. Alle Schwellen sind je Uebung aenderbar.
 */
import { addDays, startOfWeek } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';
import { GRUPPEN, GRUPPEN_IDS, beitragJeGruppe, type GruppeId, type Region } from '@/domain/training/muskeln';
import { e1rm, laeufe } from '@/domain/training/auswertung';
import { status, volumen, zielVon, type MuskelZiel, type Volumen } from '@/domain/training/muskelcheck';
import type { TrainingsProtokoll, Uebung } from '@/domain/training/typen';

/** Art und fuenf Schwellen (aufsteigend): ×Koerpergewicht (1RM) oder Wiederholungen. */
export interface KraftStandard { art: 'kg' | 'wdh'; stufen: [number, number, number, number, number] }

export const STANDARDS: Record<string, KraftStandard> = {
  'std:bankdruecken': { art: 'kg', stufen: [0.5, 0.75, 1.0, 1.5, 2.0] },
  'std:kh-bankdruecken': { art: 'kg', stufen: [0.4, 0.6, 0.8, 1.2, 1.6] },
  'std:schraegbank': { art: 'kg', stufen: [0.4, 0.6, 0.85, 1.25, 1.65] },
  'std:schulterdruecken': { art: 'kg', stufen: [0.35, 0.5, 0.7, 0.95, 1.25] },
  'std:kniebeuge': { art: 'kg', stufen: [0.75, 1.0, 1.5, 2.0, 2.5] },
  'std:frontkniebeuge': { art: 'kg', stufen: [0.6, 0.85, 1.2, 1.6, 2.0] },
  'std:kreuzheben': { art: 'kg', stufen: [1.0, 1.25, 1.75, 2.25, 3.0] },
  'std:rdl': { art: 'kg', stufen: [0.75, 1.0, 1.4, 1.8, 2.3] },
  'std:rudern-lh': { art: 'kg', stufen: [0.5, 0.75, 1.0, 1.35, 1.75] },
  'std:hipthrust': { art: 'kg', stufen: [1.0, 1.5, 2.0, 2.75, 3.5] },
  'std:bizepscurl': { art: 'kg', stufen: [0.2, 0.35, 0.5, 0.7, 0.9] },
  'std:klimmzug': { art: 'wdh', stufen: [1, 5, 10, 15, 20] },
  'std:chinup': { art: 'wdh', stufen: [1, 6, 11, 16, 21] },
  'std:dips': { art: 'wdh', stufen: [3, 10, 15, 20, 30] },
  'std:liegestuetz': { art: 'wdh', stufen: [10, 20, 30, 45, 60] },
  'std:beinheben': { art: 'wdh', stufen: [3, 8, 12, 16, 20] },
};

/** Stufe -> Wert: unter Stufe 1 bis 2, dann linear 2/4/6/8/10. */
export function wertAusStufen(x: number, stufen: readonly number[]): number {
  if (x <= 0) return 1;
  if (x < stufen[0]!) return 1 + x / stufen[0]!;
  for (let i = 1; i < stufen.length; i++) {
    if (x < stufen[i]!) return 2 + 2 * (i - 1) + (2 * (x - stufen[i - 1]!)) / (stufen[i]! - stufen[i - 1]!);
  }
  return 10;
}

export type Rang = 'bronze' | 'silber' | 'gold' | 'platin' | 'diamant';
export const RAENGE: Rang[] = ['bronze', 'silber', 'gold', 'platin', 'diamant'];
export const RANG_TEXT: Record<Rang, string> = { bronze: 'Bronze', silber: 'Silber', gold: 'Gold', platin: 'Platin', diamant: 'Diamant' };
export const rangVon = (wert: number): Rang => RAENGE[Math.min(4, Math.max(0, Math.ceil(Math.round(wert) / 2) - 1))]!;

export interface Teil { wert: number | null; text: string }
export interface RangErgebnis { gruppe: GruppeId; wert: number | null; rang: Rang | null; kraft: Teil; bestaendigkeit: Teil; entwicklung: Teil }

interface Kontext {
  protokolle: TrainingsProtokoll[];
  bibliothek: ReadonlyMap<string, Uebung>;
  heute: LocalDate;
  koerpergewicht: number | null;
  ziele?: Partial<Record<GruppeId, MuskelZiel>>;
  standards?: Record<string, KraftStandard>;
  /** Vorberechnete Wochen-Volumen (Montag -> Volumen) - spart Arbeit bei allen 18 Gruppen. */
  wochenVolumen?: Map<LocalDate, Volumen>;
}

/** Bester Leistungswert einer Uebung im Zeitraum: 1RM/KG bzw. Wiederholungen bzw. Skill-Stufe. */
function bestwert(k: Kontext, u: Uebung, std: KraftStandard | undefined, von: LocalDate, bis: LocalDate): number | null {
  let best: number | null = null;
  for (const p of k.protokolle) {
    if (p.status !== 'fertig' || p.datum < von || p.datum > bis) continue;
    for (const pu of p.uebungen) {
      if (pu.uebungId !== u.id || pu.uebersprungen) continue;
      if (u.typ === 'skill' && u.stufen?.length && pu.stufe && pu.saetze.some((s) => s.erledigt)) {
        const i = u.stufen.indexOf(pu.stufe);
        if (i >= 0) best = Math.max(best ?? 0, 1 + (9 * (i + 1)) / u.stufen.length);
        continue;
      }
      for (const s of pu.saetze) {
        if (!s.erledigt || s.art === 'aufwaermen') continue;
        if (std?.art === 'kg' && k.koerpergewicht && s.gewicht && s.wdh) best = Math.max(best ?? 0, e1rm(s.gewicht, s.wdh) / k.koerpergewicht);
        if (std?.art === 'wdh' && s.wdh) best = Math.max(best ?? 0, s.wdh);
      }
    }
  }
  return best;
}

/** Kraftwert einer Gruppe: der beste Wert ihrer Uebungen mit Schwellen (Hauptmuskel in der Gruppe) bzw. Skill-Stufen. */
function kraftWert(k: Kontext, g: GruppeId, von: LocalDate, bis: LocalDate): { wert: number; uebung: Uebung; roh: number } | null {
  let best: { wert: number; uebung: Uebung; roh: number } | null = null;
  for (const u of k.bibliothek.values()) {
    if (!beitragJeGruppe(u.hauptmuskeln, []).has(g)) continue;
    const std = k.standards?.[u.id] ?? STANDARDS[u.id];
    if (!std && !(u.typ === 'skill' && u.stufen?.length)) continue;
    const roh = bestwert(k, u, std, von, bis);
    if (roh === null) continue;
    const wert = std ? wertAusStufen(roh, std.stufen) : roh;
    if (!best || wert > best.wert) best = { wert, uebung: u, roh };
  }
  return best;
}

const z1 = (v: number) => v.toLocaleString('de-DE', { maximumFractionDigits: 2 });

export function rangFuer(k: Kontext, g: GruppeId): RangErgebnis {
  const ende = addDays(startOfWeek(k.heute), -1);
  const von12 = addDays(startOfWeek(k.heute), -84);
  const kraft = kraftWert(k, g, von12, k.heute);
  const kraftTeil: Teil = kraft
    ? { wert: kraft.wert, text: `${kraft.uebung.name}: ${k.standards?.[kraft.uebung.id]?.art === 'wdh' || STANDARDS[kraft.uebung.id]?.art === 'wdh' ? `${z1(kraft.roh)} Wdh` : kraft.uebung.typ === 'skill' ? 'Skill-Stufe' : `${z1(kraft.roh)} × Körpergewicht (1RM geschätzt)`}` }
    : { wert: null, text: k.koerpergewicht ? 'keine Übung mit Schwellen trainiert' : 'Körpergewicht fehlt bzw. keine passende Übung' };

  // Bestaendigkeit: 12 abgeschlossene Wochen, gezaehlt ab der ersten Woche mit Daten.
  const wochen = Array.from({ length: 12 }, (_, i) => addDays(startOfWeek(k.heute), -7 * (12 - i)));
  const erste = k.protokolle.filter((p) => p.status === 'fertig').map((p) => p.datum).sort()[0];
  const relevant = erste ? wochen.filter((w) => addDays(w, 6) >= erste) : [];
  const wv = (w: LocalDate) => k.wochenVolumen?.get(w) ?? volumen(k.protokolle, k.bibliothek, w, addDays(w, 6));
  const imZiel = relevant.filter((w) => { const s = status(wv(w).gruppen[g], zielVon(k.ziele, g)); return s === 'im_ziel' || s === 'ueber'; }).length;
  const best: Teil = relevant.length >= 4
    ? { wert: 1 + (9 * imZiel) / relevant.length, text: `${imZiel} von ${relevant.length} Wochen im Ziel` }
    : { wert: null, text: 'weniger als 4 Wochen Daten' };

  // Entwicklung: Kraft der letzten 4 Wochen gegen 8-4 Wochen davor.
  const jetzt = kraftWert(k, g, addDays(ende, -27), k.heute);
  const vorher = kraftWert(k, g, addDays(ende, -55), addDays(ende, -28));
  const entw: Teil = jetzt && vorher && vorher.wert > 0
    ? (() => { const d = (jetzt.wert - vorher.wert) / vorher.wert; return { wert: Math.min(10, Math.max(1, 5 + d * 50)), text: `${d >= 0 ? '+' : ''}${Math.round(d * 100)} % in 4 Wochen` }; })()
    : { wert: null, text: 'zu wenige Vergleichswerte' };

  const teile: [Teil, number][] = [[kraftTeil, 0.5], [best, 0.3], [entw, 0.2]];
  const da = teile.filter(([t]) => t.wert !== null);
  const genug = best.wert !== null && (kraftTeil.wert !== null || entw.wert !== null);
  const wert = genug ? da.reduce((s, [t, w]) => s + t.wert! * w, 0) / da.reduce((s, [, w]) => s + w, 0) : null;
  const gerundet = wert === null ? null : Math.round(wert * 10) / 10;
  return { gruppe: g, wert: gerundet, rang: gerundet === null ? null : rangVon(gerundet), kraft: kraftTeil, bestaendigkeit: best, entwicklung: entw };
}

export function alleRaenge(k: Kontext): RangErgebnis[] {
  const wochenVolumen = new Map<LocalDate, Volumen>();
  for (let i = 1; i <= 12; i++) {
    const w = addDays(startOfWeek(k.heute), -7 * i);
    wochenVolumen.set(w, volumen(k.protokolle, k.bibliothek, w, addDays(w, 6)));
  }
  return GRUPPEN_IDS.map((g) => rangFuer({ ...k, wochenVolumen }, g));
}

/** Rangwert einer Gruppe am Ende jeder der letzten `n` Wochen - fuer den Verlauf. */
export function rangVerlauf(k: Kontext, g: GruppeId, n = 12): { woche: LocalDate; wert: number | null }[] {
  return Array.from({ length: n }, (_, i) => {
    const woche = addDays(startOfWeek(k.heute), -7 * (n - 1 - i));
    return { woche, wert: rangFuer({ ...k, heute: addDays(woche, 6), wochenVolumen: undefined }, g).wert };
  });
}

/** Durchschnittsrang gesamt und je Region (nur eingestufte Gruppen). */
export function gesamtRang(raenge: RangErgebnis[]): Record<'gesamt' | Region, number | null> {
  const mittel = (xs: RangErgebnis[]) => { const w = xs.filter((r) => r.wert !== null); return w.length ? Math.round((w.reduce((s, r) => s + r.wert!, 0) / w.length) * 10) / 10 : null; };
  return {
    gesamt: mittel(raenge),
    oberkoerper: mittel(raenge.filter((r) => GRUPPEN[r.gruppe].region === 'oberkoerper')),
    unterkoerper: mittel(raenge.filter((r) => GRUPPEN[r.gruppe].region === 'unterkoerper')),
    rumpf: mittel(raenge.filter((r) => GRUPPEN[r.gruppe].region === 'rumpf')),
  };
}

/**
 * Ausdauer-Rang (getrennt, weil nicht auf Muskeln abbildbar): Mittel aus
 * Tempo (bester Lauf ab 5 km, letzte 12 Wochen) und Umfang (Ø km je Woche,
 * letzte 4 Wochen). Eigene, runde Richtwerte - keine veroeffentlichte Norm.
 */
export const LAUF_TEMPO = [420, 360, 315, 270, 225]; // s/km: 7:00 … 3:45
export const LAUF_KM = [5, 10, 20, 35, 50];

export function ausdauerRang(protokolle: TrainingsProtokoll[], heute: LocalDate): { wert: number | null; text: string } {
  const von = addDays(startOfWeek(heute), -84);
  const alle = laeufe(protokolle).filter((l) => l.datum >= von && l.datum <= heute);
  const lang = alle.filter((l) => l.km >= 5);
  if (!lang.length) return { wert: null, text: 'noch kein Lauf ab 5 km' };
  const tempo = Math.min(...lang.map((l) => l.pace));
  // Schnelleres Tempo = kleinere Zahl: umdrehen, damit die Stufen aufsteigen.
  const tempoWert = wertAusStufen(1 / tempo, LAUF_TEMPO.map((s) => 1 / s));
  const vier = addDays(startOfWeek(heute), -28);
  const km = alle.filter((l) => l.datum >= vier).reduce((s, l) => s + l.km, 0) / 4;
  const kmWert = wertAusStufen(km, LAUF_KM);
  const wert = Math.round(((tempoWert + kmWert) / 2) * 10) / 10;
  return { wert, text: `bestes Tempo ab 5 km ${Math.floor(tempo / 60)}:${String(tempo % 60).padStart(2, '0')} /km, Ø ${km.toLocaleString('de-DE', { maximumFractionDigits: 1 })} km je Woche` };
}

/** Gemerkte Rangwerte je Gruppe (nur eingestufte). */
export const rangStand = (raenge: RangErgebnis[]): Partial<Record<GruppeId, number>> =>
  Object.fromEntries(raenge.filter((r) => r.wert !== null).map((r) => [r.gruppe, r.wert!]));

/** Gruppen, die gegenueber dem gemerkten Stand einen Rang hoeher stehen. */
export function aufstiege(raenge: RangErgebnis[], gemerkt: Partial<Record<GruppeId, number>>): RangErgebnis[] {
  return raenge.filter((r) => {
    const alt = gemerkt[r.gruppe];
    return r.rang !== null && alt !== undefined && RAENGE.indexOf(r.rang) > RAENGE.indexOf(rangVon(alt));
  });
}

/**
 * Muss der gemerkte Stand still nachgezogen werden (neu eingestuft oder
 * abgestiegen)? Gruppen, die gerade nicht eingestuft sind, behalten ihren
 * gemerkten Wert - sonst loeste ein fehlendes Koerpergewicht spaeter eine
 * falsche oder gar keine Aufstiegsmeldung aus.
 */
export function rangStandGeaendert(raenge: RangErgebnis[], gemerkt: Partial<Record<GruppeId, number>>): boolean {
  return Object.entries(rangStand(raenge)).some(([g, w]) => {
    const alt = gemerkt[g as GruppeId];
    return alt === undefined || rangVon(w) !== rangVon(alt);
  });
}

/** Neuer gemerkter Stand: aktuelle Werte, nicht eingestufte Gruppen behalten den alten. */
export const rangStandMerken = (raenge: RangErgebnis[], gemerkt: Partial<Record<GruppeId, number>>) => ({ ...gemerkt, ...rangStand(raenge) });
